/**
 * After a new release, a tab opened earlier keeps running the old screens: this asks it to reload. The server
 * says which release it serves (/api/config → version), checked every few minutes and when the tab is shown again.
 */
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { APP_NAME } from "@brainlog/shared";
import { get } from "@/lib/api";
import { Button } from "@/components/ui/button";

// A part of the old release that no longer exists on the server: load the new one (once, not in a loop).
window.addEventListener("vite:preloadError", (e) => {
  try {
    if (Date.now() - Number(sessionStorage.getItem("bl_reloaded_at") ?? 0) < 60_000) return;
    sessionStorage.setItem("bl_reloaded_at", String(Date.now()));
  } catch {
    return;
  }
  e.preventDefault();
  window.location.reload();
});

export function UpdateNotice() {
  const first = useRef<string | null>(null);
  const [updated, setUpdated] = useState(false);
  const q = useQuery({
    queryKey: ["app-version"],
    queryFn: () => get<{ version?: string }>("/api/config"),
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: true,
    staleTime: 60_000,
  });
  const version = q.data?.version;
  useEffect(() => {
    if (!version || version === "dev") return;
    if (first.current === null) first.current = version;
    else if (version !== first.current) setUpdated(true);
  }, [version]);
  if (!updated) return null;
  return (
    <div role="status" className="fixed inset-x-4 bottom-4 z-50 mx-auto flex max-w-md items-center gap-3 rounded-xl border bg-card p-3 text-sm shadow-lg">
      <RefreshCw className="size-4 shrink-0 text-primary" aria-hidden />
      <span className="min-w-0 flex-1">{APP_NAME} has been updated. Reload to use the new version.</span>
      <Button size="sm" onClick={() => window.location.reload()}>
        Reload
      </Button>
    </div>
  );
}
